// FE-SCRM-050 — Prospect alerts section.
//
// Mounts the DATA-SCRM-019 alert list filtered by ``prospect_id``.
// Each row carries an acknowledge button; on success the row falls
// out (the BE filters acknowledged rows by default and we invalidate
// the query). Severity drives the chip color so urgent alerts pop
// without forking new tokens.

import { useState } from "react";

import { Card } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import {
  useAcknowledgeAlertMutation,
  useProspectAlertsQuery,
} from "./hooks";
import type { ProspectAlertRow } from "./types";


// Producer-flavoured one-liners for each alert type so the rail doesn't
// read like an enum dump.
const ALERT_TYPE_LABEL: Record<string, string> = {
  participants_increased: "Participants increased",
  participants_decreased: "Participants decreased",
  carrier_changed: "Carrier changed",
  carrier_added: "Carrier added",
  carrier_removed: "Carrier dropped",
  new_plan_filed: "New plan filed",
  plan_dropped: "Plan dropped",
  late_filing: "Filed late",
  filing_recovered: "Filing recovered",
  provider_changed: "Service provider changed",
  compensation_changed: "Compensation changed",
};


export interface ProspectAlertsSectionProps {
  prospect_id: string;
}


export function ProspectAlertsSection({
  prospect_id,
}: ProspectAlertsSectionProps) {
  const query = useProspectAlertsQuery(prospect_id);
  const ack = useAcknowledgeAlertMutation(prospect_id);
  const [pending_id, setPendingId] = useState<string | null>(null);

  if (query.isLoading) {
    return (
      <Card data-testid="prospect-alerts-section">
        <DashboardLoadingState variant="card" />
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card data-testid="prospect-alerts-section">
        <DashboardErrorState
          title="Couldn't load alerts"
          retry={() => query.refetch()}
        />
      </Card>
    );
  }

  const rows = query.data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <Card data-testid="prospect-alerts-section">
        <SectionHeader count={0} />
        <DashboardEmptyState
          title="No active alerts"
          description="Form 5500 change signals appear here as they arrive."
        />
      </Card>
    );
  }

  function handleAck(id: string) {
    setPendingId(id);
    ack.mutate(id, { onSettled: () => setPendingId(null) });
  }

  return (
    <Card
      data-testid="prospect-alerts-section"
      className="flex flex-col gap-3"
    >
      <SectionHeader count={rows.length} />
      <ul
        data-testid="prospect-alerts-list"
        className="flex flex-col gap-2"
      >
        {rows.map((row) => (
          <AlertItem
            key={row.alert_id}
            row={row}
            on_acknowledge={handleAck}
            is_pending={pending_id === row.alert_id}
          />
        ))}
      </ul>
    </Card>
  );
}


function SectionHeader({ count }: { count: number }) {
  return (
    <header className="flex flex-wrap items-baseline justify-between gap-2">
      <div className="flex flex-col gap-0.5">
        <h3 className="text-md font-semibold text-text-primary">
          Prospect alerts
        </h3>
        <p className="text-xs text-text-secondary">
          Form 5500 change signals on this account.
        </p>
      </div>
      {count > 0 ? (
        <span className="rounded-full bg-recessed px-2 py-0.5 text-[11px] uppercase tracking-wide text-text-tertiary">
          {count} active
        </span>
      ) : null}
    </header>
  );
}


function AlertItem({
  row, on_acknowledge, is_pending,
}: {
  row: ProspectAlertRow;
  on_acknowledge: (id: string) => void;
  is_pending: boolean;
}) {
  const tone = severityTone(row.severity);
  const label = ALERT_TYPE_LABEL[row.alert_type] ?? row.alert_type;

  return (
    <li
      data-testid={`prospect-alert-${row.alert_id}`}
      className={[
        "flex items-start justify-between gap-3 rounded-md border px-3 py-2",
        tone.container,
      ].join(" ")}
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span
            className={[
              "rounded-sm px-1 text-[9px] font-semibold uppercase tracking-wider",
              tone.badge,
            ].join(" ")}
          >
            {row.severity}
          </span>
          <span className="text-sm font-medium text-text-primary">
            {label}
          </span>
        </div>
        {row.triggered_at ? (
          <time className="text-xs text-text-tertiary">
            {row.triggered_at.slice(0, 10)}
          </time>
        ) : null}
      </div>
      <button
        type="button"
        data-testid={`prospect-alert-ack-${row.alert_id}`}
        disabled={is_pending}
        onClick={() => on_acknowledge(row.alert_id)}
        className="rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:bg-recessed disabled:opacity-60"
      >
        {is_pending ? "Acknowledging…" : "Acknowledge"}
      </button>
    </li>
  );
}


function severityTone(s: ProspectAlertRow["severity"]) {
  if (s === "urgent") {
    return {
      container: "border-rose-soft bg-rose-soft/40",
      badge: "bg-rose-strong text-white",
    };
  }
  if (s === "signal") {
    return {
      container: "border-amber-soft bg-amber-soft/40",
      badge: "bg-amber-strong text-white",
    };
  }
  return {
    container: "border-border-subtle bg-recessed",
    badge: "bg-recessed text-text-secondary",
  };
}
